// COUNTRY GATE (client side) — mirror of server/server/utils/countryGate.js.
// See THAILAND.md §11.0. Components must branch on the gate's getters only;
// never on a settings value directly. Prefer the profile over identity checks:
//   $features.<flag>  — capability on/off  (e.g. $features.singpass)
//   $profile          — the full country card (config/countries.js)
//   $country          — EFFECTIVE country code ('SG' unless the gate is open)
//   $isTH             — legacy boolean, kept for existing checks
//
// Resolved once at boot from /api/country-gate, which applies the gate:
// settings.country = 'TH'. (Until 2026-09-18 it also required
// is_production = 'no'; that build-phase catch was removed at launch.)
//
// FAIL-SAFE: anything other than a clear open-gate answer — network error,
// timeout, unexpected payload — resolves to SG, i.e. today's Singapore UI.

import axios from "axios";
import { profileFor, DEFAULT_COUNTRY } from "../config/countries";

// `country` is EFFECTIVE: it only leaves 'SG' when the gate is OPEN (double
// safety applied server-side), so every consumer inherits the fail-safe.
// minFlAge: minimum registration age from settings.min_fl_age, resolved with
// the gate because the DOB year dropdowns need it before they render. 16 is
// SG's long-standing value and the fallback whenever the API cannot be reached.
const state = { country: DEFAULT_COUNTRY, isTH: false, minFlAge: 16 };

// Non-component access to the resolved profile. Vue 2 FILTERS are called
// without a component context (`this` is undefined), so they cannot use
// `this.$profile` — they import this instead. Same fail-safe value.
export function currentProfile() {
  return profileFor(state.country);
}

export function installCountryGate(Vue) {
  // Non-reactive read of the resolved value. Reactivity isn't needed: the gate
  // is fixed for the life of the page (a settings flip is picked up on reload).
  Object.defineProperty(Vue.prototype, "$isTH", {
    get() {
      return state.isTH;
    },
  });
  Object.defineProperty(Vue.prototype, "$country", {
    get() {
      return state.country;
    },
  });
  Object.defineProperty(Vue.prototype, "$profile", {
    get() {
      return profileFor(state.country);
    },
  });
  // Minimum age a freelancer may register at. Drives the DOB year dropdowns
  // (newest selectable year = current year - $minFlAge) so the form can never
  // offer a date the server-side gate will reject.
  Object.defineProperty(Vue.prototype, "$minFlAge", {
    get() {
      return state.minFlAge;
    },
  });
  Object.defineProperty(Vue.prototype, "$features", {
    get() {
      return profileFor(state.country).features;
    },
  });
}

// Hard ceiling on how long app mount may wait for the gate. The app is mounted
// either way once this elapses — a slow or unreachable API must never be able to
// hold the page blank (it only ever costs us the SG default, which is correct on
// every current deployment).
const GATE_TIMEOUT_MS = 1200;

// Resolve before the app mounts so the very first render already knows the
// country (Phase 1 needs this — the locale must be right on first paint).
// Always resolves, never rejects; a failure simply leaves the gate closed.
export function resolveCountryGate() {
  const request = axios
    .get(process.env.VUE_APP_SERVER_URL + "/api/country-gate", {
      timeout: GATE_TIMEOUT_MS,
    })
    .then((res) => {
      const data = res && res.data && res.data.data ? res.data.data : null;
      if (data) {
        state.isTH = data.is_th === true;
        // Effective country: only honour the settings country when the gate is
        // OPEN, so a failed or closed gate always renders SG.
        state.country = state.isTH ? data.country || DEFAULT_COUNTRY : DEFAULT_COUNTRY;
        // Applies in BOTH countries — it is a settings value, not TH-only
        // behaviour, so it is read regardless of whether the gate is open.
        const age = parseInt(data.min_fl_age, 10);
        if (Number.isFinite(age) && age > 0) state.minFlAge = age;
      }
    })
    .catch(() => {
      // Gate stays closed — SG behaviour.
    });

  // Whichever finishes first wins; the timer guarantees the app mounts.
  const ceiling = new Promise((done) => setTimeout(done, GATE_TIMEOUT_MS));

  return Promise.race([request, ceiling]).then(() => {
    // Proving use (Phase 0): an invisible marker so the gate can be verified
    // end-to-end in DevTools without any user-visible change either way.
    if (state.isTH && document && document.body) {
      document.body.setAttribute("data-country", "th");
    }
    return state;
  });
}
